import { useState, useEffect, useCallback, useRef } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import IntegrationService from "../../../../services/integration_service";
import {
    evolutionApiDefaults,
    openAiDefaults,
    geminiDefaults,
} from "../../../../models/integration/integrationDefaults";
import { toast } from "react-toastify";

const DEFAULT_CONFIG_MAP = {
    openai: openAiDefaults,
    gemini: geminiDefaults,
    evolution: evolutionApiDefaults,
};

/**
 * useIntegrations
 * Hook que gerencia o estado e as ações da página de Integrações.
 */
export const useIntegrations = () => {
    const { idTenant, user, loading: tenantLoading } = useTenant();

    const [integrations, setIntegrations] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [evolutionStatus, setEvolutionStatus] = useState(null); // null | 'checking' | 'open' | 'close' | 'error'
    const pollingRef = useRef(null);
    const activeProviderRef = useRef(null);

    const loadIntegrations = useCallback(async () => {
        if (!idTenant) return;
        try {
            setIsLoading(true);
            const all = await IntegrationService.getAll(idTenant);
            const map = {};
            all.forEach((i) => { map[i.provider] = i; });
            setIntegrations(map);
        } catch (error) {
            console.error("Erro ao carregar integrações:", error);
            toast.error("Erro ao carregar integrações.");
        } finally {
            setIsLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        if (idTenant) loadIntegrations();
    }, [idTenant, loadIntegrations]);

    /**
     * Mescla defaults com o que veio do Firestore.
     */
    const getProviderConfig = useCallback(
        (providerId) => {
            const saved = integrations[providerId];
            const defaults = DEFAULT_CONFIG_MAP[providerId] || {};
            if (saved) {
                return { ...saved, config: { ...defaults, ...(saved.config || {}) } };
            }
            return { provider: providerId, name: "", isActive: false, config: { ...defaults } };
        },
        [integrations]
    );

    /**
     * Salva as configurações de um provider.
     */
    const handleSave = useCallback(
        async (data) => {
            if (!idTenant) return false;
            try {
                setIsSaving(true);
                const existing = integrations[data.provider];
                const payload = { ...data, id: existing?.id || data.provider };
                await IntegrationService.save(payload, idTenant, user);
                toast.success("Integração salva com sucesso!");
                await loadIntegrations();
                return true;
            } catch (error) {
                console.error("Erro ao salvar integração:", error);
                toast.error("Erro ao salvar a integração.");
                return false;
            } finally {
                setIsSaving(false);
            }
        },
        [idTenant, user, integrations, loadIntegrations]
    );

    /**
     * Testa a conexão / envio com um provider.
     */
    const handleTest = useCallback(
        async (providerId, testInput) => {
            setTestResult(null);
            setIsTesting(true);

            const config = integrations[providerId]?.config;
            if (!config?.apiKey) {
                setTestResult({ success: false, message: "Salve a API Key antes de testar." });
                setIsTesting(false);
                return;
            }

            try {
                const result = await IntegrationService.testConnection(providerId, config, testInput);
                setTestResult(result);
            } catch (error) {
                console.error("Erro no teste:", error);
                setTestResult({ success: false, message: `Erro de conexão: ${error.message}` });
            } finally {
                setIsTesting(false);
            }
        },
        [integrations]
    );

    const clearTestResult = useCallback(() => setTestResult(null), []);

    /**
     * Verifica o status da Evolution API uma vez.
     */
    const checkEvolutionStatus = useCallback(async () => {
        const config = integrations.evolution?.config;
        if (!config?.apiKey || !config?.apiUrl || !config?.instanceName) {
            setEvolutionStatus(null);
            return;
        }
        try {
            setEvolutionStatus("checking");
            const result = await IntegrationService.checkEvolutionStatus(config);
            setEvolutionStatus(result.ok ? result.state : "error");
        } catch {
            setEvolutionStatus("error");
        }
    }, [integrations.evolution?.config]);

    /**
     * Inicia/para o polling de status da Evolution quando o provider ativo muda.
     */
    const setActiveProvider = useCallback((providerId) => {
        activeProviderRef.current = providerId;

        // Limpa polling anterior
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        if (providerId === "evolution") {
            checkEvolutionStatus();
            pollingRef.current = setInterval(checkEvolutionStatus, 30000); // 30s
        } else {
            setEvolutionStatus(null);
        }
    }, [checkEvolutionStatus]);

    // Cleanup do polling ao desmontar
    useEffect(() => {
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    return {
        integrations,
        isLoading,
        isSaving,
        isTesting,
        testResult,
        evolutionStatus,
        getProviderConfig,
        handleSave,
        handleTest,
        clearTestResult,
        setActiveProvider,
        isReady: !tenantLoading && !!idTenant,
    };
};
